/**
 * A host's file drawn as a paper page (the prototype's `.paper`): the file's own first lines and its number of pages,
 * read from the file itself, in the browser, when its card comes into view. A PDF is read with pdf.js (a code-split
 * chunk, fetched only when a PDF card shows); a text or Markdown file is read as words. Anything else, or a file that
 * cannot be read, has no lines and no page count: the paper then shows the host's name and the title alone.
 */
import { type RefObject, useEffect, useRef, useState } from "react";

/** What a paper card draws from the file. */
export interface PaperFacts {
  lines: string[];
  /** A PDF's number of pages; null for a file that has no pages to count. */
  pages: number | null;
}

/** One run of text on a PDF page, and where it sits (PDF units, from the bottom left). */
export interface TextRun {
  str: string;
  x: number;
  y: number;
}

/** The prototype's paper shows four lines under the title. */
export const PAPER_LINES = 4;
/** Runs this close in height sit on one line. */
const SAME_LINE = 2.5;
// Why: the paper draws its own boxes, so the document's own bullets and boxes are left off.
const MARKS = /^[\s•·*\-–—▪▫◦○●□■☐☑☒✓✔>#]+|^\[[ xX]?\]\s*/u;

const tidy = (line: string) => line.replace(/\s+/g, " ").replace(MARKS, "").replace(MARKS, "").trim();
const same = (a: string, b: string) => a.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "") === b.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");

/** The first lines of a page of text, top to bottom, without the title the card already shows. */
function firstLines(lines: readonly string[], title: string): string[] {
  const out: string[] = [];
  for (const raw of lines) {
    const line = tidy(raw);
    if (!line || same(line, title)) continue;
    out.push(line);
    if (out.length === PAPER_LINES) break;
  }
  return out;
}

/** A PDF page's runs as its lines, read top to bottom and left to right. */
export function pdfLines(runs: readonly TextRun[], title: string): string[] {
  const rows: { y: number; runs: TextRun[] }[] = [];
  for (const r of runs) {
    if (!r.str.trim()) continue;
    const row = rows.find((w) => Math.abs(w.y - r.y) <= SAME_LINE);
    if (row) row.runs.push(r);
    else rows.push({ y: r.y, runs: [r] });
  }
  const lines = rows
    .toSorted((a, b) => b.y - a.y)
    .map((w) =>
      w.runs
        .toSorted((a, b) => a.x - b.x)
        .map((r) => r.str)
        .join(" "),
    );
  return firstLines(lines, title);
}

/** A text or Markdown file's first lines. */
export function textLines(text: string, title: string): string[] {
  return firstLines(text.split(/\r?\n/), title);
}

/** How a file is read: a PDF, words, or not at all. */
export function paperKind(name: string): "pdf" | "text" | null {
  const n = name.toLowerCase();
  if (n.endsWith(".pdf")) return "pdf";
  if (/\.(txt|md)$/.test(n)) return "text";
  return null;
}

async function readPdf(url: string, title: string): Promise<PaperFacts> {
  const [{ getDocument, GlobalWorkerOptions, VerbosityLevel }, worker] = await Promise.all([
    import("pdfjs-dist/legacy/build/pdf.mjs"),
    import("pdfjs-dist/legacy/build/pdf.worker.min.mjs?url"),
  ]);
  GlobalWorkerOptions.workerSrc = worker.default;
  // Why errors only: the words are all the card reads, so a font it would need to draw the page is no matter.
  const task = getDocument({ url, verbosity: VerbosityLevel.ERRORS });
  const doc = await task.promise;
  try {
    const page = await doc.getPage(1);
    const content = await page.getTextContent();
    const runs = content.items.flatMap((it) => ("str" in it ? [{ str: it.str, x: it.transform[4], y: it.transform[5] }] : []));
    return { lines: pdfLines(runs, title), pages: doc.numPages };
  } finally {
    void task.destroy();
  }
}

async function readText(url: string, title: string): Promise<PaperFacts> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return { lines: textLines(await res.text(), title), pages: null };
}

/** Read once per file and title in a visit: the same handout can show on several cards. */
const read = new Map<string, Promise<PaperFacts | null>>();

export function readPaper(url: string, name: string, title: string): Promise<PaperFacts | null> {
  const kind = paperKind(name);
  if (!kind) return Promise.resolve(null);
  const key = `${url}\n${title}`;
  let p = read.get(key);
  if (!p) {
    p = (kind === "pdf" ? readPdf(url, title) : readText(url, title)).catch(() => null);
    read.set(key, p);
  }
  return p;
}

/** The facts of a file, read once its card is near the screen. */
export function usePaper(url: string | null, name: string, title: string): { ref: RefObject<HTMLDivElement | null>; facts: PaperFacts | null } {
  const ref = useRef<HTMLDivElement>(null);
  const [facts, setFacts] = useState<PaperFacts | null>(null);
  useEffect(() => {
    if (!url || !paperKind(name)) return;
    let alive = true;
    const go = () =>
      void readPaper(url, name, title).then((f) => {
        if (alive) setFacts(f);
      });
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      go();
      return () => {
        alive = false;
      };
    }
    const io = new IntersectionObserver(
      (seen) => {
        if (!seen.some((e) => e.isIntersecting)) return;
        io.disconnect();
        go();
      },
      { rootMargin: "400px" },
    );
    io.observe(el);
    return () => {
      alive = false;
      io.disconnect();
    };
  }, [url, name, title]);
  return { ref, facts };
}
